/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.14.0
 */

import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import * as Yup from 'yup';
import { StringSchema } from 'yup';

import { ELocaleKeys } from '@/main/modules/internationalization/intConstants';
import { getFileExtension } from '@/main/utils/formatText';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { icons } from '@assets/index';
import { IOptionItemProps } from '@components/common/Select/Select';
import {
    API_SUBSCRIBER_ID_FILE_PARAM,
    API_SUBSCRIBER_ID_PARAM,
    API_SUBSCRIBER_ID_PERSONAL_EDIT_FILE_DOWNLOAD,
} from '@modules/app/Api';
import { EFileExtension, MapExtensionToIcon } from '@modules/app/appConstants';
import { getFormLocale } from '@modules/form/const/formLocalization';
import EFormValidation from '@modules/form/const/formValidation';
import { ICorporateFormTranslation } from '@modules/internationalization/types/corporate';

import { IPersonalDataFile, IPersonalDataNewFile, TNewFileId, TValidateFile } from '../types/file';
import { IPersonalDataInfo } from '../types/info';
import { IPersonalDataDocType, IPersonalDataPersonalDataType, PersonalDataType } from '../types/meta';

export const isPersonalDataNationalInfoRequired = (docType?: IPersonalDataDocType) =>
    !(docType?.isResident && docType.nationalityInfo) && !!docType?.id;

export const PersonalDataEditValidationSchema = (
    translation: ICorporateFormTranslation['validation'],
    docTypes: { [id: string]: IPersonalDataDocType },
    editPersonalTypes: IPersonalDataPersonalDataType,
    dataPersonalDataPassportId: number,
) => {
    Yup.setLocale(getFormLocale(translation));

    const isRequired = (option: IOptionItemProps, type: PersonalDataType) =>
        isNumber(option?.value) && docTypes[option.value]?.mandatoryAttributes.includes(type);

    const isPersonalDataRequired = (type: PersonalDataType, schema: Yup.AnySchema) =>
        editPersonalTypes.mandatoryAttributes.includes(type) ? schema.required() : schema.nullable(true);

    const getRegex = (option: IOptionItemProps, type: PersonalDataType) => {
        const anyRegex = new RegExp(/[^]*$/);
        if (!isNumber(option?.value)) {
            return anyRegex;
        }
        const regex =
            type === PersonalDataType.series ? docTypes[option.value]?.seriesRegExp : docTypes[option.value]?.numRegExp;

        if (regex) {
            return new RegExp(regex);
        }
        return anyRegex;
    };

    return Yup.object().shape({
        address: editPersonalTypes.mandatoryAttributes.includes(PersonalDataType.address)
            ? Yup.object().shape({ addressString: Yup.string().required() })
            : Yup.object().nullable(true),

        document: Yup.object().shape({
            dateOfIssue: Yup.date().when('type', {
                is: (option: IOptionItemProps) => isRequired(option, PersonalDataType.dateOfIssue),
                then: Yup.date().required(),
            }),
            divisionCode: Yup.number().when('type', {
                is: (option: IOptionItemProps) => isRequired(option, PersonalDataType.divisionCode),
                then: Yup.number().required(),
            }),
            number: Yup.string().when('type', (option: IOptionItemProps, schema: StringSchema) => {
                const regex = getRegex(option, PersonalDataType.number);
                return isRequired(option, PersonalDataType.number)
                    ? schema.required().matches(regex)
                    : schema.matches(regex);
            }),
            series: Yup.string().when('type', (option: IOptionItemProps, schema: StringSchema) => {
                const regex = getRegex(option, PersonalDataType.series);
                return isRequired(option, PersonalDataType.series)
                    ? schema.required().matches(regex)
                    : schema.matches(regex);
            }),
            providedByOrganization: Yup.string().when('type', {
                is: (option: IOptionItemProps) => isRequired(option, PersonalDataType.providedByOrganization),
                then: Yup.string().required(),
            }),
            type: Yup.object().shape({
                value: Yup.string().required(),
                label: Yup.string(),
            }),
            validFor: Yup.date().when('type', {
                is: (option: IOptionItemProps) => isRequired(option, PersonalDataType.validFor),
                then: Yup.date().required(),
            }),
        }),

        personalData: Yup.object().shape({
            birthDate: isPersonalDataRequired(PersonalDataType.birthDate, Yup.date()),
            birthPlace: isPersonalDataRequired(PersonalDataType.birthPlace, Yup.string()),
            email: isPersonalDataRequired(PersonalDataType.email, Yup.string().email()),
            name: isPersonalDataRequired(PersonalDataType.name, Yup.string()),
            patronymicIsOptional: Yup.boolean(),
            patronymic: Yup.string().when('patronymicIsOptional', {
                is: (patronymicIsOptional: boolean) => !patronymicIsOptional,
                then: isPersonalDataRequired(PersonalDataType.patronymic, Yup.string()),
            }),
            surname: isPersonalDataRequired(PersonalDataType.surname, Yup.string()),
            nationalityInfo: Yup.object().test(
                'personalData.nationalityInfo',
                translation[EFormValidation.fieldRequired],
                function () {
                    const currentDocument = get(this, 'from[2].value.document.type');
                    const value = get(this, 'parent.nationalityInfo');
                    const docType = currentDocument.value ? docTypes[currentDocument.value] : undefined;
                    return isPersonalDataNationalInfoRequired(docType)
                        ? Yup.object()
                              .shape({
                                  def: Yup.string().required(),
                                  id: Yup.string().required(),
                              })
                              .isValid(value)
                        : Yup.object().nullable(true).isValid(value);
                },
            ),
        }),
        files: Yup.array().when('document', {
            is: (document: IPersonalDataInfo['document']) =>
                document?.type?.value?.toString() !== dataPersonalDataPassportId?.toString(),
            then: editPersonalTypes.mandatoryAttributes.includes(PersonalDataType.files)
                ? Yup.array().min(1).required()
                : Yup.array().nullable(true),
            otherwise: Yup.array().nullable(true),
        }),
    });
};

export const mapSubscriberIdPersonalDataFieldConflicts = {
    [PersonalDataType.address]: 'address',
    registrationAddress: 'address',
    // personal data
    [PersonalDataType.birthDate]: 'personalData.birthDate',
    [PersonalDataType.birthPlace]: 'personalData.birthPlace',
    [PersonalDataType.email]: 'personalData.email',
    [PersonalDataType.name]: 'personalData.name',
    [PersonalDataType.nationalityInfo]: 'personalData.nationalityInfo',
    [PersonalDataType.patronymic]: 'personalData.patronymic',
    [PersonalDataType.surname]: 'personalData.surname',
    // doc
    [PersonalDataType.dateOfIssue]: 'document.dateOfIssue',
    [PersonalDataType.divisionCode]: 'document.divisionCode',
    [PersonalDataType.number]: 'document.number',
    [PersonalDataType.providedByOrganization]: 'document.providedByOrganization',
    [PersonalDataType.series]: 'document.series',
    [PersonalDataType.type]: 'document.type',
    [PersonalDataType.validFor]: 'document.validFor',
    // file
    [PersonalDataType.files]: 'files',
};

export const SubscriberIdPersonalDataFileExtensions = ['pdf', 'jpg', 'png', 'bmp', 'tiff'];

// Подтверждение данных для вкладки
export const PERSONAL_ACCOUNT_CLOSE_CODE = 'personalAccountClose';

// Валидация загружаемого файла
export const validateFile: TValidateFile = (
    file,
    alreadyUploadedFiles,
    newUploadedFiles,
    tForm,
    maxFileSize,
    maxFileNameLength,
) => {
    const fileExtension = getFileExtension(file.name);
    const uploadedFileName = [
        ...newUploadedFiles.map((file) => file.file.name),
        ...(alreadyUploadedFiles || []).map((file) => file.name),
    ];
    if (!fileExtension || !SubscriberIdPersonalDataFileExtensions.includes(fileExtension))
        return stringFormatParams(tForm.validation[EFormValidation.fileFormatIncorrect], {
            [ELocaleKeys.format]: SubscriberIdPersonalDataFileExtensions.join(', '),
        });

    if (file.size <= 0) return tForm.validation[EFormValidation.fileDataEmpty];

    if (maxFileSize && maxFileSize < file.size) return tForm.validation[EFormValidation.fileSizeExceeded];
    if (maxFileNameLength && maxFileNameLength < file.name.length)
        return stringFormatParams(tForm.validation[EFormValidation.fileNameTooLong], {
            nameLimit: maxFileNameLength,
        });

    if (uploadedFileName.includes(file.name)) return tForm.validation[EFormValidation.fileExist];

    return undefined;
};

// Присвоение типа
export function FileId(id: number) {
    return id as TNewFileId;
}

// Проверка типа добавляемого файла
export const isPersonalDataNewFile = (file: IPersonalDataFile | IPersonalDataNewFile): file is IPersonalDataNewFile =>
    'fileId' in file && typeof file.fileId === 'number';

export const getFileIcon = (file: IPersonalDataFile | File) =>
    MapExtensionToIcon[getFileExtension(file.name) as EFileExtension] || icons.defaultFileIcon;

export const getFileDownloadUrl = (file: IPersonalDataFile, subscriberId: string) =>
    stringFormatParams(API_SUBSCRIBER_ID_PERSONAL_EDIT_FILE_DOWNLOAD, {
        [API_SUBSCRIBER_ID_PARAM]: subscriberId,
        [API_SUBSCRIBER_ID_FILE_PARAM]: file.id,
    });
